import { checkFeature, checkFeatures } from '@bybeam/platform-lib/features/check';
import {
  CaseStatus,
  FeatureName,
  Nullable,
  Partner,
  PaymentMethod,
  PaymentStatus,
  ScheduleType,
} from '@bybeam/platform-types';
import { contactInfoMarkdown } from '@platform-ui-common/components/ContactInfo/index.js';
import { emailLinkMarkdown } from '@platform-ui-common/components/Link/EmailLink.js';
import { usioContactMarkdown } from '@platform-ui-common/components/UsioContact/index.js';
import { DateFormat, formatCurrency, formatDate } from '@platform-ui-common/utilities/format.js';
import {
  PaymentMethodDisplay,
  PaymentScheduleDisplay,
  PaymentStatusDisplay,
  findLatestPayment,
  getApplicantFacingPaymentStatus,
  getNextScheduledDate,
  getTotalInitiatedPaymentCount,
  hasValidScheduledPayments,
} from '@platform-ui-common/utilities/payment.js';
import { isPrepaidCard } from '@platform-ui-common/utilities/paymentMethods.js';
import { StatusBlockProps } from '@platform-ui/application/components/StatusBlock/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { programHasBeamPayments } from '@platform-ui/utilities/programs.js';
import { ApplicationStatusProps } from './index.js';

const prepaidContactInfo = (partner: Partner, method: PaymentMethod) =>
  `If you have questions regarding your ${
    method === PaymentMethod.PhysicalCard ? 'Akimbo Now ' : ''
  }prepaid card, please reach out to ${usioContactMarkdown}.
If you have questions regarding your application, please contact ${contactInfoMarkdown(partner)}.`;

const partnerContactInfo = (partner: Partner, isPayeeCurrentUser: boolean) =>
  `If you have any questions or concerns about ${
    isPayeeCurrentUser ? 'your' : 'this'
  } payment, please contact ${contactInfoMarkdown(partner)}.`;

const viewDetails = (caseId: string): StatusBlockProps['button'] => ({
  link: makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId }),
  variant: 'outline',
  children: 'View Details',
});

export default function mapPaymentsStatus(
  props: ApplicationStatusProps,
): Nullable<StatusBlockProps> {
  const { application, program, user } = props;

  const fulfillment = application?.case?.fulfillments?.[0];
  const payment = fulfillment?.payments?.[0];
  if (
    !payment ||
    !programHasBeamPayments(program) ||
    ![CaseStatus.Approved, CaseStatus.PaymentSent].includes(application.case.status)
  )
    return;

  const isPayeeCurrentUser = payment.payee?.id === user.id;
  const { status } = application.case;

  switch (true) {
    case checkFeatures(program?.features, [
      FeatureName.PaymentsMultiPayment,
      FeatureName.PaymentsPartnerIssued,
    ]) && [CaseStatus.Approved, CaseStatus.PaymentSent].includes(application.case.status):
      return ApprovedMultiPayments(props);
    case checkFeature(program?.features, FeatureName.PaymentsClaimFunds) &&
      status === CaseStatus.Approved &&
      isPayeeCurrentUser:
      return ApprovedToClaim(props);
    case checkFeature(program?.features, FeatureName.PaymentsClaimFunds) &&
      status === CaseStatus.Approved &&
      !isPayeeCurrentUser:
      return OtherPartyApprovedToClaim(props);
    case checkFeature(program?.features, FeatureName.PaymentsClaimFunds) &&
      status === CaseStatus.PaymentSent &&
      isPayeeCurrentUser:
      return CompletedClaim(props);
    case fulfillment.scheduleType !== ScheduleType.Recurring &&
      status === CaseStatus.PaymentSent &&
      isPayeeCurrentUser:
      return SentToApplicant(props);
    case fulfillment.scheduleType !== ScheduleType.Recurring &&
      status === CaseStatus.PaymentSent &&
      !isPayeeCurrentUser:
      return SentToOtherPartyOrVendor(props);
    case fulfillment.scheduleType === ScheduleType.Recurring &&
      hasValidScheduledPayments(fulfillment.payments) &&
      getTotalInitiatedPaymentCount(fulfillment.payments) === 0:
      return PaymentsScheduled(props);
    case checkFeature(program?.features, FeatureName.PaymentsPartnerIssued) &&
      status === CaseStatus.Approved:
      return PartnerIssuedStuckInApproved(props);
    case fulfillment.scheduleType === ScheduleType.Recurring:
      return RecurringPaymentsInitiated(props);
  }
}

function ApprovedMultiPayments({
  application: {
    case: { id },
  },
}: ApplicationStatusProps) {
  return {
    label: 'Status: Application Approved',
    message:
      'Your application has been approved. Select View Details below to see your payment details.',
    button: viewDetails(id),
  };
}

function ApprovedToClaim({
  application: {
    case: {
      fulfillments: [
        {
          id: fulfillmentId,
          approvedAmount,
          payments: [payment],
        },
      ],
    },
  },
  partner,
}: ApplicationStatusProps): StatusBlockProps {
  const formattedAmount = formatCurrency(approvedAmount, true);
  if (payment.status === PaymentStatus.Authorized)
    return {
      label: 'Status: Application Approved',
      message: `You are approved for funding! Click the button below to claim your ${formattedAmount}. These funds do not need to be repaid.`,
      button: {
        link: makeRoute(ClaimRoutes.PAYMENT_METHOD, { fulfillmentId }),
        children: 'Claim your funds',
      },
    };
  return {
    label: 'Status: Payment Failed',
    message: `We weren’t able to complete your claim request. This does not affect your award amount
of ${formattedAmount}. Please contact us at ${emailLinkMarkdown(partner.email)} and we
will work with you to resolve this issue as quickly as possible.`,
  };
}

function OtherPartyApprovedToClaim({
  application: {
    case: {
      fulfillments: [
        {
          approvedAmount,
          payments: [{ payee }],
        },
      ],
    },
  },
  partner,
}: ApplicationStatusProps): StatusBlockProps {
  return {
    label: 'Status: Application Approved',
    message: [
      [
        `The approved amount of ${formatCurrency(approvedAmount, true)}`,
        `has been sent to ${payee.name}.`,
        `Payment details will be available to view once it is claimed by ${payee.name}.`,
      ].join(' '),
      partnerContactInfo(partner, false),
    ].join('\n\n'),
  };
}

function CompletedClaim({
  application: {
    case: {
      id: caseId,
      fulfillments: [
        {
          approvedAmount,
          payments: [payment],
        },
      ],
    },
  },
  partner,
}: ApplicationStatusProps): StatusBlockProps {
  const formattedAmount = formatCurrency(approvedAmount, true);
  if (isPrepaidCard(payment.method))
    return {
      label: 'Status: Payment Sent',
      message: [
        payment.method === PaymentMethod.VirtualCard
          ? `Your virtual prepaid card has been created. You should have received an email from
Akimbo Card with instructions on how to access your funds.`
          : `You requested to receive your funds in the amount of ${formattedAmount} via a
physical prepaid card. You should receive the card within 7 business days.`,
        prepaidContactInfo(partner, payment.method),
      ].join('\n\n'),
      button: viewDetails(caseId),
    };

  return {
    label: 'Status: Payment Sent',
    message: [
      `The approved amount of ${formattedAmount} has been sent. Select View Details below to see your payment details.`,
      partnerContactInfo(partner, true),
    ].join('\n\n'),
    button: {
      link: makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId }),
      variant: 'outline',
      children: 'View Details',
    },
  };
}

function SentToApplicant({
  application: {
    case: {
      id: caseId,
      fulfillments: [
        {
          approvedAmount,
          payments: [payment],
        },
      ],
    },
  },
  partner,
}: ApplicationStatusProps): StatusBlockProps {
  const formattedAmount = formatCurrency(approvedAmount, true);

  let message: string;
  if (payment.method === PaymentMethod.PhysicalCard)
    message = `Your application has been approved and you should receive a physical prepaid card within
7 business days. ${prepaidContactInfo(partner, payment.method)}`;
  else if (payment.method === PaymentMethod.VirtualCard)
    message = `Your application has been approved and your virtual prepaid card has been created. You
should have received an email from Akimbo Card with instructions on how to access your
funds. ${prepaidContactInfo(partner, payment.method)}`;
  else
    message = [
      `The approved amount of ${formattedAmount} has been sent. Select View Details below to see your payment details.`,
      partnerContactInfo(partner, true),
    ].join('\n\n');

  return {
    label: 'Status: Payment Sent',
    message,
    button: viewDetails(caseId),
  };
}

function SentToOtherPartyOrVendor({
  application: {
    case: {
      id: caseId,
      fulfillments: [{ approvedAmount }],
    },
  },
  partner,
}: ApplicationStatusProps): StatusBlockProps {
  return {
    label: 'Status: Payment Sent',
    message: [
      `The approved amount of ${formatCurrency(approvedAmount, true)} has been sent. Select View Details below to see the payment details.`,
      partnerContactInfo(partner, false),
    ].join('\n\n'),
    button: viewDetails(caseId),
  };
}

function PartnerIssuedStuckInApproved({
  application: {
    case: {
      fulfillments: [
        {
          payments: [{ payee }],
        },
      ],
    },
  },
  partner,
  user,
}: ApplicationStatusProps): StatusBlockProps {
  return {
    label: 'Status: Application Approved',
    message: [
      'Congratulations! We will update you with amount and payment details once we’ve successfully initiated a payment.',
      partnerContactInfo(partner, payee.id === user.id),
    ].join(' '),
  };
}

function PaymentsScheduled({
  application: {
    case: {
      id: caseId,
      fulfillments: [
        {
          payments: [payment],
          paymentPattern,
        },
      ],
    },
  },
  partner,
  user,
}: ApplicationStatusProps): StatusBlockProps {
  const isPayeeCurrentUser = user.id === payment.payee.id;

  return {
    label: `Status: Payment 1 of ${paymentPattern.count ?? 0} Scheduled`,
    message: isPayeeCurrentUser
      ? [
          [
            'Your application has been approved and your recurring',
            PaymentScheduleDisplay[paymentPattern.pattern],
            `payment of ${formatCurrency(paymentPattern.amount, true)}`,
            'has been scheduled to be sent',
            `via ${PaymentMethodDisplay[payment.method]}.`,
            payment.method === PaymentMethod.VirtualCard &&
              'You should have received an email from Akimbo Card with instructions on how to claim your prepaid card.',
            'Select View Details below to see your payment details.',
          ]
            .filter(Boolean)
            .join(' '),
          isPrepaidCard(payment.method)
            ? prepaidContactInfo(partner, payment.method)
            : partnerContactInfo(partner, true),
        ].join('\n\n')
      : [
          [
            'Your application has been approved and a recurring',
            PaymentScheduleDisplay[paymentPattern.pattern],
            `payment of ${formatCurrency(paymentPattern.amount, true)} has been scheduled to be sent.`,
            'Select View Details below to see the payment details.',
          ]
            .filter(Boolean)
            .join(' '),
          partnerContactInfo(partner, isPayeeCurrentUser),
        ].join('\n\n'),
    button: viewDetails(caseId),
  };
}

function RecurringPaymentsInitiated({
  application: {
    case: {
      id: caseId,
      fulfillments: [{ id: fulfillmentId, payments, paymentPattern }],
    },
  },
  partner,
  user,
}: ApplicationStatusProps): StatusBlockProps {
  const latestPayment = findLatestPayment(payments);
  const currentCount = getTotalInitiatedPaymentCount(payments);
  const isPayeeCurrentUser = latestPayment.payee.id === user.id;

  if (latestPayment.status === PaymentStatus.Failed)
    return {
      label: `Status: Payment ${currentCount} of ${
        paymentPattern.count
      } ${getApplicantFacingPaymentStatus(latestPayment.status, true)}`,
      message: [
        `${isPayeeCurrentUser ? 'Your' : 'The'} most recent ${
          PaymentScheduleDisplay[paymentPattern?.pattern]
        } payment is processing.`,
        partnerContactInfo(partner, isPayeeCurrentUser),
      ].join('\n\n'),
      button: viewDetails(caseId),
    };

  return {
    label: `Status: Payment ${currentCount} of ${paymentPattern.count} ${
      PaymentStatusDisplay[latestPayment.status]
    }`,
    message: isPayeeCurrentUser
      ? [
          [
            `Your recurring ${PaymentScheduleDisplay[paymentPattern.pattern]}`,
            `payment of ${formatCurrency(paymentPattern.amount, true)}`,
            `has been sent via ${PaymentMethodDisplay[latestPayment.method]}.`,
            'Select View Details below to see your payment details.',
            currentCount === paymentPattern.count
              ? 'You have no more scheduled payments.'
              : `Your next payment is scheduled for ${formatDate(
                  getNextScheduledDate(payments),
                  DateFormat.ShortDate,
                )}.`,
          ]
            .filter(Boolean)
            .join(' '),
          isPrepaidCard(latestPayment.method)
            ? prepaidContactInfo(partner, latestPayment.method)
            : partnerContactInfo(partner, true),
        ].join('\n\n')
      : [
          [
            `A recurring ${PaymentScheduleDisplay[paymentPattern.pattern]}`,
            `payment of ${formatCurrency(paymentPattern.amount, true)} has been sent.`,
            'Select View Details below to see the payment details.',
            currentCount === paymentPattern.count
              ? 'There are no more scheduled payments.'
              : `The next payment is scheduled for ${formatDate(
                  getNextScheduledDate(payments),
                  DateFormat.ShortDate,
                )}.`,
          ]
            .filter(Boolean)
            .join(' '),
          partnerContactInfo(partner, isPayeeCurrentUser),
        ].join('\n\n'),
    button: viewDetails(caseId),
  };
}
